import React, { useEffect, useState, useContext } from "react";
import { Box, Button, Card, CardContent, InputAdornment, MenuItem, Select, TextField, Typography } from "@mui/material";
import { MainNavigation } from "../MainNavigation/MainNavigation";
import AppBar from "@mui/material/AppBar";
import { useNavigate } from "react-router-dom";
import styles from "./HospitalReports.module.css";
import theme from "../theme";
import { DataGrid, GridColDef, GridSearchIcon, GridSortModel } from "@mui/x-data-grid";
import { useQuery, useLazyQuery } from "@apollo/client/react";
import authService from "../../services/authService";
import Grid from "@mui/material/Grid";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import SummarizeIcon from "@mui/icons-material/SummarizeOutlined";
import thm from "../theme";
import ArticleIcon from "@mui/icons-material/Article";
import ErrorPopover from "../common/ErrorPopover/ErrorPopover";
import AppContext from "../../context/AppContext";
import { downloadHelper } from "../../utils/downloadHelper";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import HospitalReportsStyledHeader from "./HospitalReportsStyledHeader";
import getHospitalReportsQuery from "./GetHospitalReportsQuery";
import getAssignedHospitalReportsQuery from "./GetAssignedHospitalReportsQuery";
import getReportTypesQuery from "./GetReportTypesQuery";
import getReportYearsQuery from "./GetReportYearsQuery";
import getHospitalReportLinkQuery from "./GetHospitalReportLinkQuery";
import { GetHospitalReportsQuery } from "./types/GetHospitalReportsQuery";
import { GetAssignedHospitalReportsQuery } from "./types/GetAssignedHospitalReportsQuery";
import { GetReportTypesQuery } from "./types/GetReportTypesQuery";
import { GetReportYearsQuery } from "./types/GetReportYearsQuery";
import { GetHospitalReportLinkQuery } from "./types/GetHospitalReportLinkQuery";
import Loading from "../Loading/Loading";
import { auditLogHelper } from "../../utils/mutationHelper";

dayjs.extend(utc);

export type HospitalReportsProps = {
  display?: string;
  ml?: number;
  style?: React.CSSProperties;
};

const createStyles = () => ({
  downloadcsv: {
    color: thm.palette.primary.main + " !important",
    marginTop: "15px",
    cursor: "pointer",
  },
  disabledDownload: {
    color: "grey",
    marginTop: "15px",
    cursor: "not-allowed",
  },
  disabledButton: {
    color: "grey",
    marginTop: "15px",
    cursor: "not-allowed",
  },
  reportButton: {
    color: thm.palette.primary.main + " !important",
    marginTop: "15px",
    cursor: "pointer",
  },
  clearButton: {
    cursor: "pointer",
  },
  header: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  welcomeLogo: {
    width: "400px",
    height: "400px",
  },
  container: {
    width: "400px",
    height: "400px",
    position: "relative",
  },
  shape: {
    borderStyle: "solid",
    msTransform: "rotate(360deg)",
    oTransform: "rotate(360deg)",
    webkitTransform: "rotate(360deg)",
    transform: "rotate(360deg)",
    borderColor: "transparent " + theme.palette.primary.dark + " transparent transparent",
    float: "right",
    right: "0px",
    height: "0px",
    width: "0px",
    borderWidth: "0 120px 120px 0",
    position: "absolute",
    display: "flex",
  },
  shapeText: {
    transform: "rotate(45deg)",
    color: "#FFF",
    fontSize: "20px",
    fontWeight: "bold",
    position: "relative",
    right: "-60px",
    top: "5px",
    textAlign: "center",
    margin: "auto",
  },
  resetButtonMob: {
    backgroundColor: "#979797 !important",
    marginLeft: "2px",
    color: "#ffffff",
  },
  clearIcon: {
    opacity: "0",
  },
  input: {
    height: "40px",
  },
  handleFilename: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    marginLeft: "10px",
  },
});

const HospitalReportsPage: React.FC<HospitalReportsProps> = () => {
  const classes = createStyles();
  const { isDesktop } = useContext(AppContext);
  const [tableData, setTableData] = useState<GetHospitalReportsQuery['getUserHospitalReports']['rows']>([]);
  const [tableDataCount, setTableDataCount] = useState<number>(0);
  const [tableDataPage, setTableDataPage] = useState<number>(0);
  const [reportid, setReportid] = useState<number[]>(authService.getHospitalReportList());
  const [assignedData, setAssignedData] = useState<GetAssignedHospitalReportsQuery['getAssignedHospitalReports']>([]);
  const [reportTypeData, setReportTypeData] = useState<GetReportTypesQuery['getReportTypes']>([]);
  const [reportYearData, setReportYearData] = useState<GetReportYearsQuery['getReportYears']>([]);
  const [filename, setFilename] = useState<string>("");
  const [selectedType, setSelectedType] = useState<number>(0);
  const [selectedYear, setSelectedYear] = useState<number>(0);
  const [sortModel, setSortModel] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState<number>(50);
  const [selectedFile, setSelectedFile] = useState<number>(0);
  const mobility = authService.isMobility();
  const offsetHeight = !isDesktop || mobility ? 162 : 138;

  const { data: assignedHospitalReportsData, loading: assignedHospitalReportsLoading, error: assignedHospitalReportsError } = useQuery<GetAssignedHospitalReportsQuery>(getAssignedHospitalReportsQuery);
  const { data: reportTypesData, loading: reportTypesLoading, error: reportTypesError } = useQuery<GetReportTypesQuery>(getReportTypesQuery);
  const { data: reportYearsData, loading: reportYearsLoading, error: reportYearsError } = useQuery<GetReportYearsQuery>(getReportYearsQuery);

  const [getHospitalReportsQry, { loading: hospitalReportsLoading, error: hospitalReportsError, data: hospitalReportsData }] = useLazyQuery<GetHospitalReportsQuery>(
    getHospitalReportsQuery,
    {
      fetchPolicy: "network-only",
    },
  );

  useEffect(() => {
    setTableData([]);
    getHospitalReportsQry({
      variables: {
        reportid: reportid,
        pageSize: pageSize,
        page: tableDataPage,
        filename: filename,
        reporttype: selectedType,
        reportyear: selectedYear,
        sort: sortModel,
      },
    });

    if (assignedHospitalReportsData) {
      setAssignedData(assignedHospitalReportsData.getAssignedHospitalReports);
    }

    if (reportTypesData) {
      setReportTypeData(reportTypesData.getReportTypes);
    }

    if (reportYearsData) {
      setReportYearData(reportYearsData.getReportYears);
    }

    if (hospitalReportsData) {
      setTableData(hospitalReportsData.getUserHospitalReports.rows);
      setTableDataCount(hospitalReportsData.getUserHospitalReports.rowCount);
    }
  }, [sortModel, tableDataPage, pageSize, assignedHospitalReportsData, reportTypesData, reportYearsData, hospitalReportsData]);

  const [getHospitalReportUrl, { loading: reportLinkLoading, error: reportLinkError }] =
    useLazyQuery<GetHospitalReportLinkQuery>(getHospitalReportLinkQuery, {
      fetchPolicy: "network-only",
    });

  const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
    if (sortModel && sortModel.length > 0) {
      setSortModel([sortModel[0].field, sortModel[0].sort || ""]);
    }
  }, []);

  const handleSearch = () => {
    getHospitalReportsQry({
      variables: {
        reportid: reportid,
        pageSize: pageSize,
        page: tableDataPage,
        filename: filename,
        reporttype: selectedType,
        reportyear: selectedYear,
        sort: sortModel,
      },
    });
  };

  const handleClear = () => {
    setReportid(authService.getHospitalReportList());
    setFilename("");
    setSelectedType(0);
    setSelectedYear(0);
    setTableData([]);
    setTableDataCount(0);
    setTableDataPage(0);
    getHospitalReportsQry({
      variables: {
        reportid: authService.getHospitalReportList(),
        pageSize: pageSize,
        page: 0,
        filename: "",
        reporttype: 0,
        reportyear: 0,
        sort: sortModel,
      },
    });
  };

  const handleDownloadClick = async (event: React.MouseEvent, params: any) => {
    const id = params.id;
    setSelectedFile(id);

    const res = await getHospitalReportUrl({
      variables: {
        id: id,
      },
    });

    if (res.data?.getHospitalReportLink.url) {
      const action = "HOSPITAL_REPORT_DOWNLOAD";
      const value = JSON.stringify({ reportid: id });
      setSelectedFile(0);

      const input: any = {
        audit: {
          value,
          action,
        }
      };
  
      auditLogHelper(input);

      fetch(res.data?.getHospitalReportLink.url)
        .then((res) => res.blob())
        .then((blob) => {
          const filename = params.row && params.row.filename ? params.row.filename : params.filename;
          downloadHelper(blob, filename);
        });
      // presign url not able to use saveas, enabled cors on s3
      // https://github.com/eligrey/FileSaver.js/issues/483
    }
  };

  const navigate = useNavigate();

  // const ALLOWED_REPORT_TYPES = [1, 13, 14, 16];
  // const isReportTypeAllowed = ALLOWED_REPORT_TYPES.includes(selectedType);
  const getAntibioReportRoute = (filename: string, type: number) =>
    type === 16
      ? `/hospital-reports/antibiogram/signal-resistance/${filename}`
      : `/hospital-reports/antibiogram-report/${filename}`;

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "",
      sortable: false,
      flex: 0.1,
      renderCell: () => {
        return (
          <>
            <ArticleIcon sx={classes.downloadcsv} fontSize="small"></ArticleIcon>
          </>
        );
      },
    },
    {
      field: "reportcreated",
      headerName: "Created",
      flex: 3,
      valueFormatter: (value) => (value ? dayjs(value).format("DD MMM YYYY HH:mm:ss") : ""),
    },
    {
      field: "filename",
      headerName: "Filename",
      flex: 8,
      renderCell(params) {
        return params?.value as string;
      },
    },
    {
      field: "",
      headerName: "Download/Print",
      sortable: false,
      flex: 1,
      align: "center",
      renderCell: (params) => {
        const filename = params.row.filename as string;
        const allowDownload = filename.endsWith(".pdf") || filename.endsWith(".xlsx") || filename.endsWith(".xlx");

        return (
          <>
            {allowDownload ? (
              reportLinkLoading && (params.id as number) === selectedFile ? (
                <Loading
                  boxStyle={{ margin: "0" }}
                  iconStyle={{ color: "grey", height: "20px", width: "20px", marginLeft: "-108px", marginTop: "15px" }}
                />
              ) : (
                <SaveAltIcon
                  sx={!reportLinkLoading ? classes.downloadcsv : classes.disabledDownload}
                  fontSize="medium"
                  onClick={(event) => {
                    if (!reportLinkLoading) {
                      handleDownloadClick(event, params);
                    }
                  }}
                ></SaveAltIcon>
              )
            ) : (
              <SummarizeIcon 
                sx={classes.reportButton}
                fontSize="medium"
                onClick={() => navigate(getAntibioReportRoute(filename, params.row.reporttype))}
              />
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      <Box>
        <AppBar elevation={0} className={styles.headerBar}>
          <MainNavigation
            showBackButton
            mainHeader={HospitalReportsStyledHeader()}
            backText="PATIENTS"
            backPath="/patients"
            hideSeparator={true}
            isHospitalReports
            displayACL={isDesktop ? true : false}
          />
        </AppBar>
        <div className={`${styles.hideForPrinting}`} style={{ height: offsetHeight }} />
      </Box>
      <Grid container sx={{ overflowY: "hidden", overflowX: "hidden", ml: 2, mr: 2, mt: 0, width: "auto" }}>
        <Grid sx={{ mt: 2 }} size={12}></Grid>
        <Grid size={{ xs: 12 }}>
          <Grid container>
            <Grid size={isDesktop ? 2.5 : 12}>
              <Select
                id="reportid"
                value={reportid.length >= 2 ? 0 : reportid[0]}
                onChange={(event) => {
                  const value: number = event.target.value as number;
                  let newList: number[] = [];

                  if (value === 0) {
                    newList = authService.getHospitalReportList();
                  } else {
                    newList.push(value);
                  }

                  setReportid(newList);
                }}
                variant="outlined"
                className={styles.DateInput}
                size="small"
                fullWidth
                disabled={assignedHospitalReportsLoading || reportTypesLoading || reportYearsLoading || hospitalReportsLoading || reportLinkLoading}
                sx={{
                  "& .MuiSelect-select": {
                    width: "80%",
                  },
                }}
              >
                <MenuItem value={0}>Select Hospital Report</MenuItem>
                {assignedData.map((item: GetAssignedHospitalReportsQuery['getAssignedHospitalReports'][0]) => {
                  return (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid>
            <Grid sx={{ paddingRight: "10px" }} size={isDesktop ? 2.5 : 12}>
              <Select
                id="reporttype"
                value={selectedType}
                onChange={(event) => {
                  const value: number = event.target.value as number;
                  setSelectedType(value);
                }}
                variant="outlined"
                className={styles.DateInput}
                size="small"
                fullWidth
                disabled={assignedHospitalReportsLoading || reportTypesLoading || reportYearsLoading || hospitalReportsLoading || reportLinkLoading}
                sx={{
                  "& .MuiSelect-select": {
                    width: "80%",
                  },
                }}
              >
                <MenuItem value={0}>Select Report Type</MenuItem>
                {reportTypeData.map((item: GetReportTypesQuery['getReportTypes'][0]) => {
                  return (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid>
            <Grid sx={{ paddingRight: "10px" }} size={isDesktop ? 2.5 : 12}>
              <Select
                id="reportyear"
                value={selectedYear}
                onChange={(event) => {
                  const value: number = event.target.value as number;
                  setSelectedYear(value);
                }}
                variant="outlined"
                className={styles.DateInput}
                size="small"
                fullWidth
                disabled={assignedHospitalReportsLoading || reportTypesLoading || reportYearsLoading || hospitalReportsLoading || reportLinkLoading}
              >
                <MenuItem value={0}>Select Year</MenuItem>
                {reportYearData.map((item: GetReportYearsQuery['getReportYears'][0]) => {
                  return (
                    <MenuItem key={item.id} value={item.id}>
                      {item.reportyear}
                    </MenuItem>
                  );
                })}
              </Select>
            </Grid>
            <Grid sx={{ paddingRight: "10px" }} size={isDesktop ? 2.5 : 12}>
              <TextField
                placeholder="Filename"
                id="filename"
                name="filaname"
                variant="outlined"
                className={styles.DateInput}
                onChange={(event) => {
                  setFilename(event.target.value);
                }}
                autoComplete="off"
                onKeyDown={(e) => {
                  if (e.keyCode === 13) {
                    //Enter Key
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                size="small"
                fullWidth
                value={filename}
                disabled={assignedHospitalReportsLoading || reportTypesLoading || reportYearsLoading || hospitalReportsLoading || reportLinkLoading}
                slotProps={{
                  input: {
                    sx: classes.input,
                    startAdornment: (
                      <InputAdornment position="start">
                        <GridSearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={isDesktop ? 2 : 12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={styles.submitButton}
                onClick={handleSearch}
                disabled={assignedHospitalReportsLoading || reportTypesLoading || reportYearsLoading || hospitalReportsLoading || reportLinkLoading}
              >
                Search
              </Button>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                className={styles.submitButton}
                onClick={handleClear}
                disabled={assignedHospitalReportsLoading || reportTypesLoading || reportYearsLoading || hospitalReportsLoading || reportLinkLoading}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid sx={{ mt: 2 }} size={12}></Grid>
        <Grid sx={{ mr: 1, ml: 1, minHeight: "70vh", margin: "0 0 0 8px" }} size={12}>
          <Box style={{ height: "100%", width: "100%", border: "none" }}>
            {isDesktop ? (
              <DataGrid
                sx={{
                  ".hightBackground": { background: "var(--mui-palette-primary-light)" },
                  "&, [class^=MuiDataGrid]": { border: "none" },
                  "& .MuiDataGrid-iconSeparator": {
                    display: "none",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    border: "none",
                  },
                  "& .MuiDataGrid-main & .MuiDataGrid-root": {
                    display: "none",
                  },
                  "& .MuiTablePagination-select": { paddingRight: "24px" },
                  "& .MuiDataGrid-columnHeader:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-columnHeader--last": {
                    background: "white",
                    "@media (min-width:1024px)": {
                      paddingLeft: "9px",
                    },
                    "@media (min-width:1440px)": {
                      paddingLeft: "10px",
                    },
                    "@media (min-width:1920px)": {
                      paddingLeft: "25px",
                    },
                    "@media (min-width:2560px)": {
                      paddingLeft: "48px",
                    },
                  },
                }}
                getRowClassName={(params) => {
                  return params.row.viewCount == 0 ? "hightBackground" : "";
                }}
                paginationModel={{ page: tableDataPage, pageSize: pageSize }}
                onPaginationModelChange={(paginationModel) => {
                  setTableDataPage(paginationModel.page);
                  setPageSize(paginationModel.pageSize);
                }}
                disableColumnMenu
                rows={tableData}
                columns={columns}
                paginationMode="server"
                rowCount={tableDataCount}
                loading={assignedHospitalReportsLoading || reportTypesLoading || reportYearsLoading || hospitalReportsLoading || reportLinkLoading}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
              />
            ) : (
              <Box>
                {assignedHospitalReportsLoading || reportTypesLoading || reportYearsLoading || hospitalReportsLoading || reportLinkLoading ? (
                  <Loading boxStyle={{ margin: "0" }} iconStyle={{ color: "primary", marginTop: "15px" }} />
                ) : (
                  tableData.map((data, index) => (
                    <Card key={index} className={styles.mobileCardLayout}>
                      <CardContent className={styles.mobileCard}>
                        <Box className={styles.mobileRow}>
                          <Box>
                            <ArticleIcon sx={classes.downloadcsv} fontSize="small"></ArticleIcon>
                          </Box>
                          <Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography variant="body2">Created</Typography>
                              <Typography variant="body2">
                                {data.reportcreated ? dayjs(data.reportcreated).format("DD MMM YYYY HH:mm:ss") : ""}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box className={styles.mobileCell}>
                          <Typography variant="body2">Filename</Typography>
                          <Typography variant="body2" sx={classes.handleFilename}>
                            {data.filename as string}
                          </Typography>
                        </Box>
                        <Box className={styles.mobileCell}>
                          <Typography variant="body2">Download/View</Typography>
                          {data.filename.endsWith(".pdf") ||
                          data.filename.endsWith(".xlsx") ||
                          data.filename.endsWith(".xlx") ? (
                            reportLinkLoading && data.id === selectedFile ? (
                              <Loading
                                boxStyle={{ margin: "0" }}
                                iconStyle={{
                                  color: "grey",
                                  height: "20px",
                                  width: "20px",
                                  marginLeft: "-26px",
                                  marginTop: "15px",
                                }}
                              />
                            ) : (
                              <SaveAltIcon
                                sx={
                                  (data.filename.endsWith(".pdf") ||
                                    data.filename.endsWith(".xlsx") ||
                                    data.filename.endsWith(".xlx")) &&
                                  !reportLinkLoading
                                    ? classes.downloadcsv
                                    : classes.disabledDownload
                                }
                                fontSize="medium"
                                onClick={(event) => {
                                  if (
                                    (data.filename.endsWith(".pdf") ||
                                      data.filename.endsWith(".xlsx") ||
                                      data.filename.endsWith(".xlx")) &&
                                    !reportLinkLoading
                                  ) {
                                    handleDownloadClick(event, data);
                                  }
                                }}
                              ></SaveAltIcon>
                            )
                          ) : (
                            <SummarizeIcon
                              sx={classes.reportButton}
                              fontSize="medium"
                              onClick={() => navigate(getAntibioReportRoute(data.filename, selectedType))}
                              style={{ cursor: "pointer" }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Box>
            )}
            {(assignedHospitalReportsError || reportTypesError || reportYearsError || hospitalReportsError || reportLinkError) && <ErrorPopover />}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
export default HospitalReportsPage;
